import React, { useMemo } from 'react';
import { Map as MapIcon } from 'lucide-react';
import {
  Map,
  MapFitBounds,
  MapMarker,
  MapPolyline,
  MapPopup,
  MapTileLayer,
  MapZoomControl,
  MapInvalidateSize,
} from '../UI/map';
import { EmptyState } from '../UI/empty-state';
import { Skeleton } from '../UI/skeleton';
import { useLocale } from '../../contexts/LocaleContext';
import { colors } from '../../config/colors';
import { cn } from '../../lib/utils';

const DEFAULT_CENTER = [20, 0];

function SupplyChainMap({
  markers = [],
  routes = [],
  bounds = [],
  loading = false,
  geocoding = false,
  compact = false,
  minHeight = 400,
  className,
  emptyTitleKey = 'supplyMapEmptyTitle',
  emptyDescriptionKey = 'supplyMapEmptyDescription',
  onEmptyAction,
  emptyActionLabelKey,
}) {
  const { t } = useLocale();

  const fitBounds = useMemo(() => {
    const points = [];
    markers.forEach((m) => points.push(m.position));
    routes.forEach((r) => (r.positions || []).forEach((p) => points.push(p)));
    return points.length > 0 ? points : bounds;
  }, [markers, routes, bounds]);

  const showOverlay = loading;
  const isEmpty = !showOverlay && markers.length === 0 && routes.length === 0;

  if (isEmpty) {
    return (
      <EmptyState
        icon={MapIcon}
        title={t(emptyTitleKey)}
        description={t(emptyDescriptionKey)}
        actionLabel={emptyActionLabelKey ? t(emptyActionLabelKey) : undefined}
        onAction={onEmptyAction}
        className={cn('w-full', className)}
        style={{ minHeight }}
      />
    );
  }

  return (
    <div
      className={cn('relative z-0 h-full w-full overflow-hidden', className)}
      style={{ minHeight }}
    >
      {showOverlay && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background-card/80">
          <div className="flex flex-col items-center gap-2">
            <Skeleton className="h-8 w-48" />
            <p className="text-sm text-text-secondary">
              {geocoding ? t('supplyMapGeocoding') : t('supplyMapLoading')}
            </p>
          </div>
        </div>
      )}
      <Map
        center={DEFAULT_CENTER}
        zoom={2}
        className="h-full w-full rounded-none border-0"
        style={{ minHeight }}
      >
        <MapTileLayer variant="dark" />
        <MapZoomControl />
        <MapInvalidateSize />
        {fitBounds.length > 0 ? <MapFitBounds bounds={fitBounds} /> : null}
        {routes.map((route) => (
          <MapPolyline
            key={route.id}
            positions={route.positions}
            color={route.type === 'graph' ? colors.secondary[500] : colors.brand[500]}
          />
        ))}
        {markers.map((marker) => (
          <MapMarker
            key={marker.id}
            position={marker.position}
            iconColor={marker.iconColor || colors.brand[500]}
          >
            <MapPopup>
              <div className="flex min-w-[140px] flex-col gap-1">
                <p className="font-semibold text-text-primary">{marker.title}</p>
                {marker.subtitle ? (
                  <p className="text-xs text-text-secondary">{marker.subtitle}</p>
                ) : null}
              </div>
            </MapPopup>
          </MapMarker>
        ))}
      </Map>
      {!compact && markers.length > 0 ? (
        <div className="pointer-events-none absolute bottom-2 left-2 z-[400] rounded-sm bg-background-card/90 px-2 py-1 text-xs text-text-tertiary shadow-elevation-1">
          {t('supplyMapAttribution')}
        </div>
      ) : null}
    </div>
  );
}

export default SupplyChainMap;
